import React, { useState, useEffect } from 'react';
import { Wallet, ChevronDown, TrendingUp, TrendingDown, ArrowRight, RefreshCw, ChevronRight } from 'lucide-react';

interface AssetBalance {
  token: string;
  symbol: string;
  amount: number;
  price: number;
  value: number;
  change24h: number;
  image?: string;
}

interface PortfolioWidgetProps {
  walletAddress?: string;
  onClose?: () => void;
  isWidget?: boolean;
}

const PortfolioWidget: React.FC<PortfolioWidgetProps> = ({ 
  walletAddress, 
  onClose, 
  isWidget = false 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [assets, setAssets] = useState<AssetBalance[]>([]);
  const [sortBy, setSortBy] = useState<'value' | 'name' | 'change'>('value');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [totalValue, setTotalValue] = useState(0);
  const [totalChange, setTotalChange] = useState(0);
  
  // Generate mock portfolio data
  useEffect(() => {
    if (!walletAddress) return;
    
    setIsLoading(true);
    
    // Mock data generation
    setTimeout(() => {
      const mockTokens = [
        { token: 'Solana', symbol: 'SOL', image: 'https://cryptologos.cc/logos/solana-sol-logo.png' },
        { token: 'Bonk', symbol: 'BONK', image: 'https://cryptologos.cc/logos/bonk-bonk-logo.png' },
        { token: 'Jupiter', symbol: 'JUP', image: 'https://cryptologos.cc/logos/jupiter-jup-logo.png' },
        { token: 'Raydium', symbol: 'RAY', image: 'https://cryptologos.cc/logos/raydium-ray-logo.png' },
        { token: 'Serum', symbol: 'SRM', image: 'https://cryptologos.cc/logos/serum-srm-logo.png' },
      ];
      
      const mockAssets = mockTokens.map(token => {
        const amount = Math.random() * 100;
        const price = Math.random() * 50;
        const change24h = -15 + Math.random() * 30;
        
        return {
          ...token,
          amount,
          price,
          value: amount * price,
          change24h
        };
      });
      
      // Calculate totals
      const total = mockAssets.reduce((sum, asset) => sum + asset.value, 0);
      const weightedChange = mockAssets.reduce((sum, asset) => {
        const weight = asset.value / total;
        return sum + (asset.change24h * weight);
      }, 0);
      
      setAssets(mockAssets);
      setTotalValue(total);
      setTotalChange(weightedChange);
      setIsLoading(false);
    }, 1000);
  }, [walletAddress]);
  
  // Sort assets based on current sort criteria
  const sortedAssets = [...assets].sort((a, b) => {
    if (sortBy === 'value') {
      return sortDirection === 'asc' ? a.value - b.value : b.value - a.value;
    }
    if (sortBy === 'name') {
      return sortDirection === 'asc' 
        ? a.token.localeCompare(b.token) 
        : b.token.localeCompare(a.token);
    }
    // sort by change
    return sortDirection === 'asc' ? a.change24h - b.change24h : b.change24h - a.change24h;
  });
  
  // Handle column header click for sorting
  const handleSortClick = (column: 'value' | 'name' | 'change') => {
    if (sortBy === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default direction
      setSortBy(column);
      setSortDirection('desc');
    }
  };
  
  // Handle refresh button click
  const handleRefresh = () => {
    if (!walletAddress) return;
    
    setIsLoading(true);
    
    // Simulate refresh delay
    setTimeout(() => {
      // Update with slightly different values
      const updatedAssets = assets.map(asset => {
        const newPrice = asset.price * (0.95 + Math.random() * 0.1); // Price changes by -5% to +5%
        const newChange = asset.change24h + (-2 + Math.random() * 4); // Change shift by -2% to +2%
        
        return {
          ...asset,
          price: newPrice,
          value: asset.amount * newPrice,
          change24h: newChange
        };
      });
      
      // Recalculate totals
      const total = updatedAssets.reduce((sum, asset) => sum + asset.value, 0);
      const weightedChange = updatedAssets.reduce((sum, asset) => {
        const weight = asset.value / total;
        return sum + (asset.change24h * weight);
      }, 0);
      
      setAssets(updatedAssets);
      setTotalValue(total);
      setTotalChange(weightedChange);
      setIsLoading(false);
    }, 800);
  };
  
  // Format numbers for display
  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
  };
  
  const formatPercentage = (value: number) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  };
  
  // Format token amount based on value
  const formatTokenAmount = (amount: number) => {
    if (amount < 0.001) return amount.toFixed(8);
    if (amount < 0.01) return amount.toFixed(6);
    if (amount < 1) return amount.toFixed(4);
    if (amount < 1000) return amount.toFixed(2);
    return `${(amount / 1000).toFixed(2)}K`;
  };
  
  const content = (
    <div className="w-full h-full flex flex-col" data-widget-type="portfolio">
      {/* Header with wallet info */}
      <div className="p-3 border-b border-white/10">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium text-white flex items-center">
            <Wallet size={16} className="mr-2 text-[#47be8f]" /> 
            Portfolio Overview
          </h3>
          
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className={`p-1.5 rounded-full transition-colors ${
              isLoading ? 'text-gray-500' : 'text-gray-400 hover:text-white'
            }`}
            title="Refresh Data"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
        
        {walletAddress ? (
          <div className="mt-2 text-xs text-gray-400 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span>Wallet:</span>
              <span className="bg-white/10 px-2 py-0.5 rounded">
                {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
              </span>
            </div>
            <a href="#" className="text-[#47be8f] hover:underline flex items-center">
              View all <ChevronRight size={12} className="ml-1" />
            </a>
          </div>
        ) : (
          <div className="mt-2 text-xs text-white flex">
            <button className="bg-[#47be8f] hover:bg-[#39a677] px-3 py-1 rounded flex-1 transition-colors">
              Connect Wallet
            </button>
          </div>
        )}
      </div>
      
      {/* Total value section */}
      <div className="p-3 border-b border-white/10 flex justify-between">
        <div>
          <div className="text-xs text-gray-400">Total Value</div>
          <div className="text-white text-lg font-semibold">
            {isLoading ? '...' : formatCurrency(totalValue)}
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-xs text-gray-400">24h Change</div>
          <div className={`font-semibold ${
            totalChange >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {isLoading ? '...' : formatPercentage(totalChange)}
          </div>
        </div>
      </div>
      
      {/* Assets table */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#47be8f]"></div>
          </div>
        ) : !walletAddress ? (
          <div className="h-full flex items-center justify-center text-center p-4">
            <div>
              <Wallet size={32} className="mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-400">Connect your wallet to view your portfolio</p>
            </div>
          </div>
        ) : sortedAssets.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center p-4">
            <div>
              <Wallet size={32} className="mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-400">No assets found in this wallet</p>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#182828] text-xs uppercase text-gray-400">
              <tr>
                <th 
                  className="p-3 text-left cursor-pointer hover:text-white"
                  onClick={() => handleSortClick('name')}
                >
                  <div className="flex items-center">
                    Asset
                    {sortBy === 'name' && (
                      <ChevronDown 
                        size={12} 
                        className={`ml-1 ${sortDirection === 'asc' ? 'rotate-180' : ''} transition-transform`} 
                      />
                    )}
                  </div>
                </th>
                <th className="p-3 text-right">Amount</th>
                <th 
                  className="p-3 text-right cursor-pointer hover:text-white"
                  onClick={() => handleSortClick('value')}
                >
                  <div className="flex items-center justify-end">
                    Value
                    {sortBy === 'value' && (
                      <ChevronDown 
                        size={12} 
                        className={`ml-1 ${sortDirection === 'asc' ? 'rotate-180' : ''} transition-transform`} 
                      />
                    )}
                  </div>
                </th>
                <th 
                  className="p-3 text-right cursor-pointer hover:text-white"
                  onClick={() => handleSortClick('change')}
                >
                  <div className="flex items-center justify-end">
                    24h
                    {sortBy === 'change' && (
                      <ChevronDown 
                        size={12} 
                        className={`ml-1 ${sortDirection === 'asc' ? 'rotate-180' : ''} transition-transform`} 
                      />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {sortedAssets.map((asset) => (
                <tr key={asset.symbol} className="hover:bg-white/5">
                  <td className="p-3">
                    <div className="flex items-center">
                      {asset.image ? (
                        <img 
                          src={asset.image} 
                          alt={asset.token} 
                          className="w-5 h-5 mr-2 rounded-full"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-5 h-5 mr-2 rounded-full bg-gray-700 flex items-center justify-center">
                          {asset.symbol.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{asset.token}</div>
                        <div className="text-xs text-gray-400">{asset.symbol}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <div>{formatTokenAmount(asset.amount)}</div>
                    <div className="text-xs text-gray-400">${asset.price.toFixed(2)}</div>
                  </td>
                  <td className="p-3 text-right font-medium">
                    {formatCurrency(asset.value)}
                  </td>
                  <td className="p-3 text-right">
                    <div className={`flex items-center justify-end ${
                      asset.change24h >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {asset.change24h >= 0 ? (
                        <TrendingUp size={14} className="mr-1" />
                      ) : (
                        <TrendingDown size={14} className="mr-1" />
                      )}
                      {formatPercentage(asset.change24h)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Footer */}
      {walletAddress && (
        <div className="p-3 border-t border-white/10">
          <a 
            href="#" 
            className="text-xs flex items-center justify-center text-[#47be8f] hover:underline"
            onClick={(e) => e.preventDefault()}
          >
            View transaction history <ArrowRight size={12} className="ml-1" />
          </a>
        </div>
      )}
    </div>
  );
  
  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">
        {content}
      </div>
    );
  }
  
  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-dark-grey border border-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet size={18} className="text-primary" />
          <h2 className="text-lg font-bold text-white">Portfolio</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            &times;
          </button>
        )}
      </div>
      
      {content}
    </div>
  );
};

// Factory function to create a canvas widget
export const createPortfolioWidget = () => {
  return {
    type: 'portfolio',
    title: 'Portfolio Overview',
    content: <PortfolioWidget isWidget={true} />,
    size: { width: 500, height: 480 },
    padding: 0,
    margin: 0,
    contentStyle: { 
      padding: 0, 
      margin: 0,
      backgroundColor: 'transparent',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    },
    disablePadding: true,
    fullWidth: true,
    fullHeight: true,
    background: 'transparent',
    glassMorphism: true,
    containerClassName: 'p-0 m-0 overflow-hidden',
    containerStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      border: '1px solid rgba(36, 195, 91, 0.2)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)',
      overflow: 'hidden'
    }
  };
};

export default PortfolioWidget;
